import React from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { APP_VERSION } from '@/constants/config';
import { getMacAppVersion } from '@/lib/macAppVersion';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { isDarkTheme } from '@/constants/Colors';
import { useModalStore } from '@/store';

const GAB_AI_BASE = 'https://gab.ai';

function HelpSettings() {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const isDark = isDarkTheme(theme);
  const router = useRouter();
  const { closeModal } = useModalStore((state) => state.actions);

  const handleLinkPress = async (path) => {
    if (Platform.OS !== 'web') {
      const url = `${GAB_AI_BASE}${path}`;
      await WebBrowser.openBrowserAsync(url, {
        presentationStyle: WebBrowser.WebBrowserPresentationStyle.FULL_SCREEN,
        dismissButtonStyle: 'done',
      });
    } else {
      // Dismiss the settings modal first so the destination page isn't
      // rendered behind the still-open overlay (which looks like nothing
      // happened on click).
      closeModal();
      router.push(path);
    }
  };

  return (
    <View style={{ flex: 1, paddingHorizontal: 4 }}>
      {/* Documentation - Primary CTA */}
      <Pressable
        onPress={() => handleLinkPress('/docs')}
        style={({ hovered }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: 16,
          padding: 20,
          borderRadius: 16,
          backgroundColor: hovered
            ? (isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)')
            : (isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.06)'),
          borderWidth: 1,
          borderColor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)',
          marginBottom: 24,
        })}
      >
        <View
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            backgroundColor: '#3B82F6',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="book" size={22} color="#fff" solid />
        </View>
        <View style={{ flex: 1 }}>
          <Text size="l" weight="semibold" style={{ marginBottom: 4 }}>
            {t('settings.help.documentation')}
          </Text>
          <Text size="s" color="secondary">
            {t('settings.help.documentationSubtitle')}
          </Text>
        </View>
        <Icon name="arrow-right" size={16} color={colors.text_color_secondary} />
      </Pressable>

      {/* Support Options */}
      <Text size="xs" weight="medium" color="tertiary" style={{ marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {t('settings.help.getHelp')}
      </Text>
      
      <View
        style={{
          borderRadius: 12,
          backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          borderWidth: 1,
          borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          overflow: 'hidden',
          marginBottom: 32,
        }}
      >
        <Pressable
          onPress={() => handleLinkPress('/contact')}
          style={({ hovered }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 14,
            padding: 16,
            borderBottomWidth: 1,
            borderBottomColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            backgroundColor: hovered
              ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)')
              : 'transparent',
          })}
        >
          <View
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="headset" size={16} color="#10B981" solid />
          </View>
          <View style={{ flex: 1 }}>
            <Text size="m" weight="medium">{t('settings.help.contactSupport')}</Text>
            <Text size="xs" color="secondary">{t('settings.help.contactSupportSubtitle')}</Text>
          </View>
          <Icon name="arrow-up-right-from-square" size={12} color={colors.text_color_tertiary} />
        </Pressable>

        <Pressable
          onPress={() => handleLinkPress('/feedback')}
          style={({ hovered }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 14,
            padding: 16,
            backgroundColor: hovered
              ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)')
              : 'transparent',
          })}
        >
          <View
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              backgroundColor: isDark ? 'rgba(139, 92, 246, 0.15)' : 'rgba(139, 92, 246, 0.1)',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="comment" size={16} color="#8B5CF6" solid />
          </View>
          <View style={{ flex: 1 }}>
            <Text size="m" weight="medium">{t('settings.help.submitFeedback')}</Text>
            <Text size="xs" color="secondary">{t('settings.help.submitFeedbackSubtitle')}</Text>
          </View>
          <Icon name="arrow-up-right-from-square" size={12} color={colors.text_color_tertiary} />
        </Pressable>
      </View>

      {/* Legal Links - De-emphasized */}
      <Text size="xs" weight="medium" color="tertiary" style={{ marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {t('settings.help.legal')}
      </Text>
      
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 32 }}>
        {[
          { title: t('settings.help.termsOfService'), url: '/terms-of-service' },
          { title: t('settings.help.privacyPolicy'), url: '/privacy-policy' },
          { title: t('settings.help.termsOfSale'), url: '/terms-of-sale' },
          { title: 'CCPA', url: '/ccpa' },
          { title: 'DMCA', url: '/dmca' },
        ].map((link) => (
          <Pressable
            key={link.title}
            onPress={() => handleLinkPress(link.url)}
            style={({ hovered }) => ({
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: 8,
              backgroundColor: hovered
                ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)')
                : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'),
            })}
          >
            <Text size="xs" color="secondary">{link.title}</Text>
          </Pressable>
        ))}
      </View>

      {/* Version - Subtle footer */}
      <View style={{ alignItems: 'center', paddingVertical: 16 }}>
        <Text size="xs" color="tertiary">{t('settings.help.version')} {Platform.OS === 'macos' ? getMacAppVersion() : APP_VERSION}</Text>
      </View>
    </View>
  );
} 

export default React.memo(HelpSettings);
